import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Security as SecurityIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

interface Role {
  id: string;
  name: string;
  code: string;
  description: string;
  hierarchy: number;
  isActive: boolean;
  isSystem: boolean;
  schoolId: string | null;
  _count: {
    permissions: number;
    users: number;
  };
}

const getDisplayCode = (code: string) => {
  const value = String(code || '').toUpperCase();
  if (value === 'ADMIN') return 'PROTECTED_ADMIN';
  return value.replace(/^ADMIN_/, '').replace(/^STAFF_/, '');
};

const RolesList: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get('/system-settings/roles');
      setRoles(
        (response.data || []).filter(
          (r: Role) => r.code !== 'SUPER_ADMIN' && r.id !== user?.role?.id,
        ),
      );
    } catch (error) {
      console.error('Error fetching roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (role: Role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!roleToDelete) return;
    setActionLoading(true);
    try {
      await axios.delete(`/system-settings/roles/${roleToDelete.id}`);
      fetchRoles();
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    } catch (error) {
      console.error('Error deleting role:', error);
      alert('Failed to delete role. It might be assigned to users.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/system-settings/user-roles')} sx={{ mr: 1 }}>
              Back
            </Button>
            <SecurityIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              Roles & Permissions
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/system-settings/user-roles/roles/create')}
          >
            Create Role
          </Button>
        </Box>

        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Role Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell align="center">Hierarchy</TableCell>
                  <TableCell align="center">Permissions</TableCell>
                  <TableCell align="center">Users Assigned</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">{role.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {role.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                        <Chip 
                          label={getDisplayCode(role.code)} 
                          size="small" 
                          variant="outlined" 
                          color={role.isSystem ? "primary" : "default"}
                        />
                    </TableCell>
                    <TableCell align="center">
                      {role.code === 'ADMIN' ? 2 : role.code.startsWith('ADMIN_') ? 3 : role.code.startsWith('STAFF_') ? 4 : role.hierarchy}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={role._count.permissions} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={role._count.users} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={role.isActive ? 'Active' : 'Inactive'}
                        color={role.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/system-settings/user-roles/roles/edit/${role.id}`)}
                            disabled={role.isSystem}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(role)}
                            color="error"
                            disabled={role.isSystem || role._count.users > 0}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && roles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No roles found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Dialog open={deleteDialogOpen} onClose={() => !actionLoading && setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Role</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete role <strong>{roleToDelete?.name}</strong>?
              <br /><br />
              This action cannot be undone. You can only delete roles that are not assigned to any users.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={confirmDelete} color="error" variant="contained" disabled={actionLoading}>
              {actionLoading ? 'Deleting...' : 'Delete Role'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </MainLayout>
  );
};

export default RolesList;
